import {
  isTimeoutError,
  // TError,
  // TInit,
  // TRequest,
  // TResponse,
  // TRetry,
  type MerrRequest,
} from '@setplex/merr'
import { retry as retried } from '~/shared/opentelemetry/model/http'
import {
  RETRIABLE_ERRORS,
  RETRIABLE_METHODS,
  RETRY_LIMIT,
  TOTAL_RETRY_LIMIT_MILLIS,
} from './index.h'
import { type RequestPolicy } from './request'
import { calculateBackoffMillis, logger, toURL } from './lib'
import { HTTP_LATENCY } from '../index.h'

/**
 * Retry Policy implementation
 * @see https://setplexapps.atlassian.net/wiki/spaces/MW/pages/2682454084/Retry+Policy
 */
export class RetryPolicy {
  constructor(private requestPolicy: RequestPolicy) {
    this.callback = this.callback.bind(this) // bind callback to the instance
  }

  /**
   * Retry policy callback, used in Merr,
   * to determine if request should be retried or not
   */
  callback(
    attempt: number,
    minDelay: number, // this is a `Retry-After` header absolute value (or 0 if not set)
    request: MerrRequest,
    response?: Response,
    error?: Error
  ): number | false {
    // biome-ignore format: nah
    // logger.log('🟣', request.__?.timings?.map((t) => { switch (true) {
    //   case t instanceof TInit: return `init/${t}`
    //   case t instanceof TRequest: return `request/${t}`
    //   case t instanceof TResponse: return `response/${t}`
    //   case t instanceof TRetry: return `retry/${t}`
    //   case t instanceof TError: return `error/${t}`
    //   default: return `unknown/${t}`
    // }}))

    // do not retry on timeout error
    // maybe change in future
    if (isTimeoutError(error)) {
      return false
    }

    // TypeError usually means was failed to create due to invalid arguments,
    // or request was blocked by the browser (CORS, CSP, etc.),
    // or fetch throws a network error.
    // either way, it is pointless to retry it
    // https://developer.mozilla.org/en-US/docs/Web/API/Window/fetch#exceptions
    if (error && (error as unknown) instanceof TypeError) {
      return false
    }

    // do not retry on non-retriable http response codes
    if (response && !RETRIABLE_ERRORS.includes(response.status)) {
      return false
    }

    // do not retry on non-retriable http methods
    if (request && !RETRIABLE_METHODS.includes(request.method.toUpperCase())) {
      return false
    }

    // limit retries count
    if (attempt >= RETRY_LIMIT) {
      return false
    }

    // get backoff from Request Policy bucket, it is hard limit,
    // and Retry Policy under any circumstances cannot send new request before it
    const now = Date.now()
    const { bucket } = this.requestPolicy.getBucket(request)
    const limit = bucket?.limit ?? now
    const requestBackoff = limit - now

    // calculate soft retry backoff
    const retryBackoff = calculateBackoffMillis(attempt)

    // get minimal allowed backoff
    const backoff = Math.max(requestBackoff, retryBackoff, minDelay)

    // calculate total request time, passed to this moment
    // first element in `timings` array is initial request time
    const init = Number(request.__?.timings?.[0]) || now
    const total = now - init

    const url = toURL(request)
    const surl = url ? url.pathname + url.search : '-'

    // do not retry if new request time will exceed TOTAL_RETRY_LIMIT_MILLIS
    if (total + backoff + HTTP_LATENCY > TOTAL_RETRY_LIMIT_MILLIS) {
      logger.debug(`⏹️ do not retry %c${surl}%c due to long await time`, 'color:teal', 'color:inherit') // biome-ignore format: one line
      return false
    }

    // emit retry event, to be used in telemetry
    retried({ request, response, error, attempt })

    logger.debug(`🔄 retry %c${surl}%c in ${backoff}ms`, 'color:teal', 'color:inherit') // biome-ignore format: one line
    return backoff
  }
}
